/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { events } from '@adobe-commerce/event-bus';
import { ImageProps, Image, ProductItemCard } from '@adobe-commerce/elsie/components';
import { htmlStringDecode } from '@/plp/utils/htmlStringDecode';
import { Scope, SearchVariables } from '@/plp/data/models';
import { Product } from '@/plp/data/models/product';
import { useText } from '@adobe-commerce/elsie/i18n';
import { SimpleProductPrice } from '@/plp/containers/SearchResults/SimpleProductPrice';
import { ComplexProductPrice } from "@/plp/containers/SearchResults/ComplexProductPrice";
import { ProductList, SearchAlertMessage } from '@/plp/components'
import {
  searchProductClick,
  searchResultsView,
  categoryResultsView,
  PLP_UNIT_ID,
  SEARCH_UNIT_ID
} from '@/plp/utils/acdlEvents';

type SlotDefaultContext = {
  product: Product;
  variables: SearchVariables | null;
}

export interface SearchResultsProps extends HTMLAttributes<HTMLDivElement> {
  routeProduct?: (product: Product) => string;
  scope?: Scope;
  imageWidth?: number;
  imageHeight?: number;
  skeletonCount?: number;
  onSearchResult?: (payload: Product[]) => void;
  slots?: {
    ProductActions?: SlotProps<SlotDefaultContext>;
    ProductPrice?: SlotProps<SlotDefaultContext>;
    ProductName?: SlotProps<SlotDefaultContext>;
    ProductImage?: SlotProps<SlotDefaultContext & { defaultImageProps: ImageProps }>;
    NoResults?: SlotProps<{ error: string | null; variables: SearchVariables | null }>;
    Header?: SlotProps<{ products: Product[]; variables: SearchVariables | null }>;
    Footer?: SlotProps<{ products: Product[]; variables: SearchVariables | null }>;
  },
}

export const SearchResults: Container<SearchResultsProps> = ({
  routeProduct,
  scope,
  slots,
  imageWidth = 400,
  imageHeight = 450,
  skeletonCount = 8,
  onSearchResult,
}) => {
  const acdlUnitId = scope === 'popover' ? SEARCH_UNIT_ID : PLP_UNIT_ID;

  const labels = useText({
    noResults: 'Search.PLP.noResults',
    searchError: 'Search.PLP.searchError',
  });

  const [variables, setVariables] = useState<SearchVariables | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadingEvent = events.on('search/loading', setLoading, { eager: true, scope });

    const errorEvent = events.on('search/error', () => {
      setError(labels.searchError!);
    }, { eager: true, scope });

    const searchEvent = events.on('search/result', (payload) => {
      setError(payload.result?.items.length < 1 ? labels.noResults! : null);

      setVariables(payload.request);
      setItems(payload.result?.items);
      onSearchResult?.(payload.result?.items);

      // Publish search results view event to ACDL
      if (payload.request.phrase) {
        searchResultsView(acdlUnitId);
      } else {
        categoryResultsView(acdlUnitId);
      }
    }, { eager: true, scope });

    return () => {
      loadingEvent?.off();
      errorEvent?.off();
      searchEvent?.off();
    };
  }, []);

  const handleProductClick = (event: MouseEvent, product: Product): void => {
    const anchor = (event.target as HTMLElement)?.closest('a');

    if (!anchor || !routeProduct) return;

    const anchorUrl = new URL(anchor.href);
    const routeUrl = new URL(routeProduct(product), window.location.origin);

    // Compare both pathname and hostname to ensure same path under same host
    if (anchorUrl.pathname === routeUrl.pathname && anchorUrl.hostname === routeUrl.hostname) {    
      searchProductClick(product.sku, acdlUnitId);
    }
  };

  const renderProductImageSlot = (product: Product, index: number) => {
    const defaultImageProps: ImageProps = {
      loading: index < 8 ? 'eager' : 'lazy',
      src: product.images?.[0]?.url || '',
      alt: product.images?.[0]?.label || '',
      width: imageWidth,
      height: imageHeight,
      params: { width: imageWidth },
    };
    const productName = product.name !== null ? htmlStringDecode(product.name) : product.sku;
    const imageComponent = (
      <Image class="product-discovery-product-item__image" {...defaultImageProps} aria-label={productName || ''} />
    );

    return (
      <Slot name="ProductImage" slot={slots?.ProductImage} context={{ product, defaultImageProps, variables }}>
        {routeProduct ? (
          <a href={routeProduct(product)}>
            {imageComponent}
          </a>
        ) : (
          imageComponent
        )}
      </Slot>
    );
  };

  const renderProductNameSlot = (product: Product) => {
    const productName = product.name !== null ? htmlStringDecode(product.name) : '';
    return (
      <Slot name="ProductName" slot={slots?.ProductName} context={{ product, variables }}>
        {routeProduct ? (
          <a href={routeProduct(product)}>
            {productName}
          </a>
        ) : (
          productName
        )}
      </Slot>
    );
  };

  const renderProductPriceSlot = (product: Product) => {
    const PriceNode =
      product.typename === "ComplexProductView" ? (
        <ComplexProductPrice product={product} />
      ) : (
        <SimpleProductPrice product={product} />
      );
  
    return (
      <Slot name="ProductPrice" slot={slots?.ProductPrice} context={{ product, variables }}>
        {routeProduct ? (
          <a href={routeProduct(product)}>{PriceNode}</a>
        ) : (
          PriceNode
        )}
      </Slot>
    );
  };  
  
  const renderActionsSlot = (product: Product) => {
    return <Slot name="ProductActions" slot={slots?.ProductActions} context={{ product, variables }} />;
  };

  const renderAlertSlot = () => {
    return <Slot name="NoResults" slot={slots?.NoResults} context={{ error, variables }}>
      {error && <SearchAlertMessage alertMessage={error} />}
    </Slot>;
  }

  const renderSkeleton = () => {
    return <ProductList
      imageWidth={imageWidth}
      imageHeight={imageHeight}
      productList={Array.from({ length: skeletonCount }).map((_, index) => (
        <ProductItemCard key={index} initialized={false} />
      ))}
    />
  }

  const renderHeader = () => {
    return <Slot name="Header" slot={slots?.Header} context={{ products: items, variables }} />;
  }

  const renderFooter = () => {
    return <Slot name="Footer" slot={slots?.Footer} context={{ products: items, variables }} />;
  }

  const renderResults = () => {
    if (error) return renderAlertSlot();

    return <ProductList
      header={renderHeader()}
      footer={renderFooter()}
      imageWidth={imageWidth}
      imageHeight={imageHeight}
      productList={items.map((product, index) => (
        <ProductItemCard key={product.id || product.sku || index}
          image={renderProductImageSlot(product, index)}
          titleNode={renderProductNameSlot(product)}
          price={renderProductPriceSlot(product)}
          actionButton={slots?.ProductActions ? renderActionsSlot(product) : undefined}
          onClick={(event) => handleProductClick(event, product)}
          initialized
        />
      ))}
    />
  }

  return (
    <div>
      {loading ? renderSkeleton() : renderResults()}
    </div>
  );
};
